/**
 *
 * CommunicationNewMobile
 *
 */

import React, { memo, useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Helmet } from 'react-helmet';
import { createStructuredSelector } from 'reselect';
import { compose } from 'redux';

// styles
import './style.scss';

import { useInjectSaga } from 'utils/injectSaga';
import { useInjectReducer } from 'utils/injectReducer';
import reducer from './reducer';
import saga from './saga';

import { flushState, updateCommunication } from './actions';
import {
  // init
  makeSelectFirstRender,
  makeSelectItemDetail,
  // update
  makeSelectUpdateLoading,
  makeSelectUpdateSuccess,
  makeSelectUpdateError,
} from './selectors';

// antd component
import { Layout } from 'antd';
const { Content } = Layout;

// assets
import { ArrowLeftOutlined } from '@ant-design/icons';

// components
import Preloader from 'components/Preloader';


// inner communications components
import { CreateParentCommunication } from './components';

// contexts
import {
  useGetLocalUser,
  useGetLocalActiveChild,
  useGetUserChildren,
} from 'engine/Contexts/User.context';
// utils
import { getObjId } from 'utils/helpers/helper.general';

export function CommunicationNewMobile({
  // main data
  itemDetail,
  // states
  history,
  updateLoading,
  updateSuccessful,
  updateError,
  firstRender,
  // actions
  createCommunication,
  flushState,
}) {
  useInjectReducer({ key: 'CommunicationNewMobile', reducer });
  useInjectSaga({ key: 'CommunicationNewMobile', saga });

  const localUser = useGetLocalUser()();
  const localActiveChild = useGetLocalActiveChild()();
  const children = useGetUserChildren()();

  const oldParams = history.location?.state?.oldParams;
  const goBack = history.location?.state?.goBack;
  const goBackTitle = history.location?.state?.goBackTitle;

  const [currentStudent, setCurrentStudent] = useState(children?.find(s => getObjId(s) == getObjId(localActiveChild)));

  useEffect(() => {
    return () => {
      // Anything in here is fired on component unmount.
      flushState();
    };
  }, []);

  // if local student changed
  useEffect(() => {
    if (getObjId(currentStudent) != getObjId(localActiveChild)) {
      setCurrentStudent(children?.find(s => getObjId(s) == getObjId(localActiveChild)));
    }
  }, [localActiveChild?._id]);

  const onGoBackHandler = useCallback(() => { 
    history.push({
      pathname: goBack ? goBack : `/communications`,
      search: '',
      hash: '',
      state: {
        oldParams: oldParams,
        goBack: '/communications',
        cameFrom: 'new_communication',
      },
    })
  }, [goBack, oldParams, goBackTitle]);

  const onSaveHandler = useCallback((dataForSave) => { 
    createCommunication(dataForSave);
  }, []);

  const studentName = useCallback((stdObj, crop) => {
    const stdNameStr = stdObj?.description
      ? `${stdObj?.description?.lastname} ${
          crop && stdObj?.description?.firstname
            ? stdObj?.description?.firstname.substring(0, 1) + '.'
            : stdObj?.description?.firstname
        }`
      : '';
    return (
      stdNameStr && <span className="student-name-title">{stdNameStr}</span>
    );
  }, []);


  // render function
  return (
    <Layout className="main-full-content">
      <Helmet>
        <title>Nouvelle requête for Communication</title>
        <meta name="description" content="Description of Communication" />
      </Helmet>
      <Content className="main-content-container communication-new">
        <div className="communication-new__header">
          <div
            className="go-back"
            onClick={onGoBackHandler}
          >
            <ArrowLeftOutlined /> {`Nouvelle requête ${!!currentStudent && `: `}`} {studentName(currentStudent, true)}
          </div>
        </div>
        <div className="communication-new__main">
          <CreateParentCommunication
            student={currentStudent || localActiveChild?._id}
            loading={updateLoading}
            successful={updateSuccessful}
            onGoBack={onGoBackHandler}
            onSave={onSaveHandler}
          />
        </div>
      </Content>
    </Layout>
  );
}

CommunicationNewMobile.defaultProps = {
  disabled: false
};
CommunicationNewMobile.propTypes = {
  dispatch: PropTypes.func.isRequired,
  flushState: PropTypes.func.isRequired
};

const mapStateToProps = createStructuredSelector({
  firstRender: makeSelectFirstRender(),
  itemDetail: makeSelectItemDetail(),
  // update
  updateLoading: makeSelectUpdateLoading(),
  updateSuccessful: makeSelectUpdateSuccess(),
  updateError: makeSelectUpdateError(),
});

function mapDispatchToProps(dispatch) {
  return {
    dispatch,
    flushState: () => dispatch(flushState()),
    createCommunication: itemData => dispatch(updateCommunication(itemData)),
  };
}

const withConnect = connect(
  mapStateToProps,
  mapDispatchToProps,
);

export default compose(
  withConnect,
  memo,
)(CommunicationNewMobile);
